import type { ToolApprovalItem, ToolApprovalStatus } from '@/modules/MessageParser/const';
import { create } from 'zustand';

interface ToolApprovalState {
  /** 是否处于审批状态 */
  isActive: boolean;
  /** 待审批的工具调用列表 */
  toolCalls: ToolApprovalItem[];
  /** 当前审批批次 ID，由批次内 toolCallId 集合生成 */
  approvalBatchId: string;
  /** 当前审批关联的 messageId */
  messageId: string;
  /** 当前会话 ID */
  sessionId: string;
  /** 后端计划自动同意的绝对时间戳 */
  autoApproveAt: number;
  /** 当前审批状态 */
  status: ToolApprovalStatus | null;
  /** 激活或幂等更新审批状态 */
  activate: (data: {
    toolCalls: ToolApprovalItem[];
    approvalBatchId: string;
    messageId: string;
    sessionId?: string;
    autoApproveAt: number;
  }) => void;
  startSubmitting: (approvalBatchId: string, now?: number) => boolean;
  finishManual: (approvalBatchId: string, approved: boolean) => void;
  finishAutoApproved: (approvalBatchId: string) => boolean;
  closeBatchAsHandled: (approvalBatchId: string) => void;
  closeAsHandled: (messageId: string) => void;
  reopenAfterSubmitFailure: (approvalBatchId: string) => void;
  cancel: (messageId: string) => void;
  /** 清空审批状态 */
  deactivate: () => void;
}

const TERMINAL_STATUSES = new Set<ToolApprovalStatus>([
  'approved',
  'rejected',
  'auto_approved',
  'handled',
  'cancelled',
]);

function getInitialState() {
  return {
    isActive: false,
    toolCalls: [] as ToolApprovalItem[],
    approvalBatchId: '',
    messageId: '',
    sessionId: '',
    autoApproveAt: 0,
    status: null as ToolApprovalStatus | null,
  };
}

export const useToolApprovalStore = create<ToolApprovalState>((set, get) => ({
  ...getInitialState(),

  activate: data => set((state) => {
    if (state.approvalBatchId !== data.approvalBatchId) {
      return {
        ...data,
        sessionId: data.sessionId ?? '',
        isActive: true,
        status: 'pending',
      };
    }
    if (state.status && TERMINAL_STATUSES.has(state.status)) {
      return state;
    }
    return {
      ...data,
      sessionId: data.sessionId ?? '',
      isActive: state.isActive,
      status: state.status,
    };
  }),

  startSubmitting: (approvalBatchId, now = Date.now()) => {
    const state = get();
    if (
      state.approvalBatchId !== approvalBatchId
      || state.status !== 'pending'
      || !state.isActive
      || !state.sessionId
      || now >= state.autoApproveAt
    ) {
      return false;
    }

    set({ status: 'submitting' });
    return true;
  },

  finishManual: (approvalBatchId, approved) => set((state) => {
    if (state.approvalBatchId !== approvalBatchId || state.status !== 'submitting') {
      return state;
    }
    return {
      status: approved ? 'approved' : 'rejected',
      isActive: false,
    };
  }),

  finishAutoApproved: (approvalBatchId) => {
    const state = get();
    if (
      state.approvalBatchId !== approvalBatchId
      || state.status === 'approved'
      || state.status === 'rejected'
      || state.status === 'auto_approved'
      || state.status === 'cancelled'
    ) {
      return false;
    }

    set({ status: 'auto_approved', isActive: false });
    return true;
  },

  closeBatchAsHandled: approvalBatchId => set((state) => {
    if (
      state.approvalBatchId !== approvalBatchId
      || !state.status
      || TERMINAL_STATUSES.has(state.status)
    ) {
      return state;
    }
    return { status: 'handled', isActive: false };
  }),

  closeAsHandled: messageId => set((state) => {
    if (
      state.messageId !== messageId
      || !state.status
      || TERMINAL_STATUSES.has(state.status)
    ) {
      return state;
    }
    return { status: 'handled', isActive: false };
  }),

  reopenAfterSubmitFailure: approvalBatchId => set((state) => {
    if (state.approvalBatchId !== approvalBatchId || state.status !== 'submitting') {
      return state;
    }
    return { status: 'pending', isActive: true };
  }),

  cancel: messageId => set((state) => {
    if (
      state.messageId !== messageId
      || !state.status
      || TERMINAL_STATUSES.has(state.status)
    ) {
      return state;
    }
    return { status: 'cancelled', isActive: false };
  }),

  deactivate: () => set(getInitialState()),
}));
